// src/components/NewCarFilterBar.tsx

import {
  Box,
  Button,
  TextField,
  Menu,
  MenuItem,
  IconButton,
  Typography,
  Stack,
  InputAdornment,
  useTheme,
  Tooltip,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import ClearIcon from '@mui/icons-material/Clear';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import axiosInstance from '../services/axiosInstance';
import { encodeId, decodeId } from '../utils/idEncoder';

type NewCarFilterBarProps = {
  viewMode?: 'grid' | 'list';
  onToggleViewMode?: () => void;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  url?: string;
  onFilterChange?: (key: string, value: string) => void;
};

const ENCRYPTED_KEYS = [
  'vehicleBrandIds',
  'vehicleBodyTypeIds',
  'vehicleFuelTypeIds',
  'dealerId'
];

const RangeDropdownContent = ({
  title, minParamKey, maxParamKey, options, formatLabel, onClose
}: {
  title: string; minParamKey: string; maxParamKey: string; options: number[]; formatLabel: (val: number) => string; onClose: () => void;
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);

  const currentMin = searchParams.get(minParamKey) ? Number(searchParams.get(minParamKey)) : '';
  const currentMax = searchParams.get(maxParamKey) ? Number(searchParams.get(maxParamKey)) : '';

  const [minVal, setMinVal] = useState<number | ''>(currentMin);
  const [maxVal, setMaxVal] = useState<number | ''>(currentMax);

  useEffect(() => {
    setMinVal(currentMin);
    setMaxVal(currentMax);
  }, [currentMin, currentMax]);

  const handleApply = () => {
    const params = new URLSearchParams(location.search);
    if (minVal !== '') params.set(minParamKey, minVal.toString()); else params.delete(minParamKey);
    if (maxVal !== '') params.set(maxParamKey, maxVal.toString()); else params.delete(maxParamKey);
    params.delete('page');
    navigate(`${location.pathname}?${params.toString()}`);
    onClose();
  };

  const handleClear = () => {
    setMinVal(''); setMaxVal('');
    const params = new URLSearchParams(location.search);
    params.delete(minParamKey); params.delete(maxParamKey); params.delete('page');
    navigate(`${location.pathname}?${params.toString()}`);
    onClose();
  };

  return (
    <Box p={3} width={340} onClick={(e) => e.stopPropagation()}>
      <Typography variant="subtitle2" gutterBottom fontWeight="bold">{title}</Typography>
      <Stack direction="row" spacing={2} alignItems="center" my={2}>
        <TextField select fullWidth size="small" label="Lowest" value={minVal} onChange={(e) => setMinVal(e.target.value === '' ? '' : Number(e.target.value))}>
          <MenuItem value=""><em>Any</em></MenuItem>
          {options.map(opt => <MenuItem key={`min-${opt}`} value={opt} disabled={maxVal !== '' && opt > maxVal}>{formatLabel(opt)}</MenuItem>)}
        </TextField>
        <Typography variant="body2" color="text.secondary" fontWeight={500}>to</Typography>
        <TextField select fullWidth size="small" label="Highest" value={maxVal} onChange={(e) => setMaxVal(e.target.value === '' ? '' : Number(e.target.value))}>
          <MenuItem value=""><em>Any</em></MenuItem>
          {options.map(opt => <MenuItem key={`max-${opt}`} value={opt} disabled={minVal !== '' && opt < minVal}>{formatLabel(opt)}</MenuItem>)}
        </TextField>
      </Stack>
      <Stack direction="row" spacing={1} justifyContent="space-between" mt={1}>
        <Button size="small" color="inherit" onClick={handleClear}>Clear</Button>
        <Button size="small" variant="contained" onClick={handleApply} sx={{ px: 3 }}>Apply</Button>
      </Stack>
    </Box>
  );
};

const NewCarFilterBar = ({ viewMode, onToggleViewMode, searchQuery, onSearchChange, url, onFilterChange }: NewCarFilterBarProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [fuelTypeData, setvehicleFuelTypeData] = useState<any[]>([]);
  const [bodyTypesData, setvehicleBodyTypeData] = useState<any[]>([]);
  const [brandData, setvehicleBrandData] = useState<any[]>([]);

  const [tempSelected, setTempSelected] = useState<{ [key: string]: string[] }>({});
  const [initializedKeys, setInitializedKeys] = useState<Set<string>>(new Set());

  const priceOptions = Array.from({ length: 40 }, (_, i) => (i + 1) * 10000); // 10k increments

  const handleSearchSubmit = () => {
    const params = new URLSearchParams(location.search);
    if (searchQuery.trim()) params.set('searchKeyword', searchQuery.trim());
    else params.delete('searchKeyword');
    params.delete('page');
    navigate(`${location.pathname}?${params.toString()}`);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const payload = { vehicleBodyTypeData: true, vehicleFuelTypeData: true, vehicleBrandData: true };
        const res = await axiosInstance.post("/CustomerVehicle/VehicleMultipleDataList", payload);
        const data = res.data as any;
        if (data.responseCode === "00" && data.responseData) {
          setvehicleBodyTypeData(data.responseData.vehicleBodyTypeData || []);
          setvehicleFuelTypeData(data.responseData.vehicleFuelTypeData || []);
          setvehicleBrandData(data.responseData.vehicleBrandData || []);
        }
      } catch (e) { console.error(e); }
    };
    fetchData();
  }, []);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>, filter: string) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setActiveFilter(filter);
  };
  const handleClose = () => { setAnchorEl(null); setActiveFilter(null); };

  const getTempSelected = (key: string): string[] => {
    if (!initializedKeys.has(key)) {
      const params = new URLSearchParams(location.search);
      const val = params.get(key);
      const parts = val ? val.split(',') : [];
      let decodedIds = ENCRYPTED_KEYS.includes(key) ? parts.map(hash => decodeId(hash)?.toString() || '').filter(Boolean) : parts;
      setTempSelected(prev => ({ ...prev, [key]: decodedIds }));
      setInitializedKeys(prev => new Set(prev).add(key));
      return decodedIds;
    }
    return tempSelected[key] || [];
  };

  const toggleSelection = (key: string, id: string) => {
    setTempSelected(prev => {
      const set = new Set(prev[key] || []);
      set.has(id) ? set.delete(id) : set.add(id);
      return { ...prev, [key]: Array.from(set) };
    });
  };

  const applySelection = (key: string) => {
    const rawIds = tempSelected[key] || [];
    let outputString = ENCRYPTED_KEYS.includes(key) ? rawIds.map(id => encodeId(id)).join(',') : rawIds.join(',');
    if (onFilterChange) onFilterChange(key, outputString);
    handleClose();
  };

  const clearSelection = (key: string) => {
    setTempSelected(prev => ({ ...prev, [key]: [] }));
    if (onFilterChange) onFilterChange(key, '');
    handleClose();
  };

  // Render for the Dropdown Mega Menus (Body Type, Fuel Type)
  const renderMegaMenu = (key: string, data: any[], idField: string, labelField: string, columns: number, title: string) => {
    const sortedData = [...data].sort((a, b) => (a[labelField] || '').localeCompare(b[labelField] || ''));
    return (
      <Box sx={{ width: { xs: 300, md: columns * 160 }, maxWidth: '90vw', p: 3 }}>
        <Typography variant="h6" fontWeight="800" mb={3} sx={{ display: 'flex', alignItems: 'center', fontSize: '1.2rem', color: '#2b2b2b' }}>
          <Box component="span" sx={{ width: 4, height: 22, bgcolor: 'primary.main', mr: 1.5, borderRadius: 1 }} />
          {title}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', sm: `repeat(${Math.min(columns, 3)}, 1fr)`, md: `repeat(${columns}, 1fr)` },
            columnGap: 3, rowGap: 2, maxHeight: 450, overflowY: 'auto', mb: 3, pr: 1,
            '&::-webkit-scrollbar': { width: '6px' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#e0e0e0', borderRadius: '4px' },
          }}
        >
          {sortedData.map((item) => {
            const id = item[idField].toString();
            const isSelected = getTempSelected(key).includes(id);
            return (
              <Typography
                key={id} variant="body2" onClick={() => toggleSelection(key, id)}
                sx={{
                  cursor: 'pointer',
                  color: isSelected ? 'primary.main' : 'text.secondary',
                  fontWeight: isSelected ? 800 : 500,
                  transition: 'all 0.2s ease',
                  '&:hover': { color: 'primary.main', transform: 'translateX(3px)' }
                }}
              >
                {item[labelField]}
              </Typography>
            );
          })}
        </Box>
        <Divider sx={{ mb: 2, borderColor: '#f0f0f0' }} />
        <Stack direction="row" spacing={1} justifyContent="space-between">
          <Button size="small" color="inherit" onClick={() => clearSelection(key)} sx={{ fontWeight: 600 }}>Clear</Button>
          <Button size="small" variant="contained" onClick={() => applySelection(key)} sx={{ px: 4, borderRadius: 2, fontWeight: 700 }}>Apply</Button>
        </Stack>
      </Box>
    );
  };

  // Only these 3 will be dropdown buttons now
  const dropdownFilters = ['Body Type', 'Fuel Type', 'Price Range'];
  const filterMap: any = {
    'Body Type': { key: 'vehicleBodyTypeIds', data: bodyTypesData, idField: 'vehicleBodyTypeId' },
    'Fuel Type': { key: 'vehicleFuelTypeIds', data: fuelTypeData, idField: 'vehicleFuelTypeId' },
    'Price Range': { key: 'minPrice', getLabel: (p: URLSearchParams) => p.get('minPrice') ? `$${Number(p.get('minPrice')).toLocaleString()}+` : null },
  };

  const sortedBrandData = [...brandData].sort((a, b) => (a.description || '').localeCompare(b.description || ''));

  return (
    <Box sx={{ width: '100%' }}>
      
      {/* --- ROW 1: Search Bar & Dropdown Buttons --- */}
      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ xs: 'stretch', lg: 'center' }} mb={4}>
        
        {/* Search Field */}
        <Box sx={{ flexShrink: 0, width: { xs: '100%', lg: '350px' } }}>
          <TextField
            fullWidth
            placeholder="Search Vehicles..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit()}
            size="small"
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon color="action" fontSize="small" /></InputAdornment>,
              endAdornment: (
                <InputAdornment position="end" sx={{ height: '100%', maxHeight: '100%', ml: 0 }}>
                  {searchQuery && (
                    <IconButton size="small" onClick={() => { onSearchChange(''); const p = new URLSearchParams(location.search); p.delete('searchKeyword'); navigate(`${location.pathname}?${p.toString()}`); }} sx={{ mr: 1 }}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )}
                  <Button variant="contained" color="primary" disableElevation onClick={handleSearchSubmit} sx={{ height: '100%', minWidth: '50px', borderRadius: '0 8px 8px 0', px: 0 }}><SearchIcon /></Button>
                </InputAdornment>
              )
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: 'background.paper', pr: 0, overflow: 'hidden' } }}
          />
        </Box>

        {/* Dropdown Buttons (Beside Search Bar) */}
        <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: { xs: 1, lg: 0 }, '&::-webkit-scrollbar': { display: 'none' } }}>
          {dropdownFilters.map(label => {
            const conf = filterMap[label];
            let subLabel = null;
            let isActive = false;

            if (conf) {
              const params = new URLSearchParams(location.search);
              if (conf.getLabel) subLabel = conf.getLabel(params);
              else {
                const count = (params.get(conf.key)?.split(',') || []).filter(Boolean).length;
                if (count > 0) subLabel = `${count}`;
              }
              if (subLabel) isActive = true;
            }

            return (
              <Button
                key={label}
                variant="outlined"
                size="medium"
                onClick={(e) => handleClick(e, label)}
                endIcon={<KeyboardArrowDownIcon />}
                sx={{
                  borderRadius: '24px', 
                  flexShrink: 0, 
                  px: 2.5, 
                  textTransform: 'none', 
                  fontWeight: 600,
                  borderColor: isActive ? 'primary.main' : '#e0e0e0',
                  color: isActive ? 'primary.main' : '#333333',
                  bgcolor: isActive ? 'rgba(255, 161, 0, 0.04)' : '#ffffff',
                  '&:hover': {
                    borderColor: isActive ? 'primary.main' : '#cccccc',
                    bgcolor: isActive ? 'rgba(255, 161, 0, 0.08)' : '#f9f9f9',
                  }
                }}
              >
                {label}
                {subLabel && <Box component="span" sx={{ ml: 1, bgcolor: isActive ? 'primary.main' : '#e0e0e0', color: isActive ? '#fff' : '#000', px: 0.8, py: 0.2, borderRadius: 2, fontSize: '0.7rem' }}>{subLabel}</Box>}
              </Button>
            );
          })}
        </Box>

        <Box sx={{ flexGrow: 1, display: { xs: 'none', lg: 'block' } }} />

        {/* View Mode & Clear Filters */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ justifyContent: 'flex-end' }}>
          {onToggleViewMode && (
            <Tooltip title={`Switch to ${viewMode === 'grid' ? 'List' : 'Grid'} View`}>
              <IconButton onClick={onToggleViewMode} sx={{ border: '1px solid #eee' }} size="small">
                {viewMode === 'grid' ? <ViewListIcon fontSize="small" /> : <ViewModuleIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Clear All Filters">
            <IconButton color="error" size="small" onClick={() => navigate(url || '/new-vehicles')}>
              <RestartAltIcon />
            </IconButton>
          </Tooltip>
        </Stack>

      </Stack>

      {/* --- ROW 2: Inline Always-Visible Brands --- */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" fontWeight="800" mb={3} sx={{ display: 'flex', alignItems: 'center', fontSize: '1.2rem', color: '#2b2b2b' }}>
          <Box component="span" sx={{ width: 4, height: 22, bgcolor: 'primary.main', mr: 1.5, borderRadius: 1 }} />
          Explore Vehicle Makes
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)', md: 'repeat(6, 1fr)', lg: 'repeat(8, 1fr)' },
            gap: 2,
          }}
        >
          {sortedBrandData.map((item) => {
            const id = item.vehicleBrandId.toString();
            const isSelected = getTempSelected('vehicleBrandIds').includes(id);
            return (
              <Typography
                key={id}
                variant="body2"
                onClick={() => toggleSelection('vehicleBrandIds', id)}
                sx={{
                  cursor: 'pointer',
                  color: isSelected ? 'primary.main' : 'text.secondary',
                  fontWeight: isSelected ? 800 : 500,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    color: 'primary.main',
                    transform: 'translateX(3px)'
                  }
                }}
              >
                {item.description}
              </Typography>
            );
          })}
        </Box>

        {/* Brand specific apply/clear so user can select multiple then confirm */}
        {getTempSelected('vehicleBrandIds').length > 0 && (
          <Stack direction="row" spacing={2} mt={3}>
            <Button size="small" variant="outlined" onClick={() => clearSelection('vehicleBrandIds')} sx={{ borderRadius: 2 }}>
              Clear Selection
            </Button>
            <Button size="small" variant="contained" onClick={() => applySelection('vehicleBrandIds')} sx={{ borderRadius: 2, px: 3, fontWeight: 700 }}>
              Apply Brands
            </Button>
          </Stack>
        )}
      </Box>

      {/* --- Dropdown Menus --- */}
      <Menu 
        anchorEl={anchorEl} 
        open={Boolean(anchorEl)} 
        onClose={handleClose} 
        PaperProps={{ elevation: 6, sx: { borderRadius: 3, mt: 1.5, border: '1px solid #f0f0f0' } }}
      >
        {activeFilter === 'Body Type' && renderMegaMenu('vehicleBodyTypeIds', bodyTypesData, 'vehicleBodyTypeId', 'description', 3, 'Explore Vehicle Types')}
        {activeFilter === 'Fuel Type' && renderMegaMenu('vehicleFuelTypeIds', fuelTypeData, 'vehicleFuelTypeId', 'description', 2, 'Explore Fuel Types')}
        {activeFilter === 'Price Range' && (
          <RangeDropdownContent title="Price Range" minParamKey="minPrice" maxParamKey="maxPrice" options={priceOptions} formatLabel={(v) => `$${v.toLocaleString()}`} onClose={handleClose} />
        )}
      </Menu>
    </Box>
  );
};

export default NewCarFilterBar;